import React, { useEffect, useMemo, useRef } from 'react';
import { LandingPage } from 'cpfr-common';
import { HeroImage } from '@cp-shared-19/frontend-ui';
import { ContentSection, Heading, IconList, Video } from '@vwfs-bronson/bronson-react';
import { useTranslation } from 'react-i18next';
import { CyCard, CyCarousel, CyCarouselItem } from '@vwfs-uix/core/dist/react';
import { CarouselBreakpointsProp, CarouselI18n } from '@vwfs-uix/core/dist/components/carousel/carousel';

export type LandingPageUiProps = {
    landingPage?: LandingPage;
    clickHandler?: React.MouseEventHandler;
    isAuthenticated: boolean;
    brand: string;
};

type Card = {
    title: string;
    icon: string;
    points: string[];
};

const isCard = (card: unknown): card is Card => {
    if (!card || typeof card !== 'object') {
        return false;
    }

    const candidate = card as Partial<Card>;
    return typeof candidate.title === 'string' && typeof candidate.icon === 'string' && Array.isArray(candidate.points);
};

const carouselBreakpoints: CarouselBreakpointsProp = {
    720: { itemsPerView: 1 },
    960: { itemsPerView: 2 },
    1280: { itemsPerView: 3 },
};

const brandsWithVideo = ['vw', 'vwfs', 'vwcv'];

export const LandingPageUi: React.FC<LandingPageUiProps> = ({ landingPage, clickHandler, isAuthenticated, brand }) => {
    const { t } = useTranslation('landing-page');
    const cards = useMemo<Card[]>(() => {
        const translatedCards = t('cards', { returnObjects: true }) as unknown;
        return Array.isArray(translatedCards) ? translatedCards.filter(isCard) : [];
    }, [t]);

    const carouselI18n = useMemo<CarouselI18n>(
        () => ({
            buttonNextLabel: t('carousel.buttonNextLabel'),
            buttonPrevLabel: t('carousel.buttonPrevLabel'),
            containerLabel: t('carousel.containerLabel'),
            fullscreenEnterLabel: '',
            fullscreenExitLabel: '',
            goToItemX: (str: string) => t('carousel.goToItemX', { itemNumber: str }),
            paginationContainerLabel: t('carousel.paginationContainerLabel'),
            paginationFirstLabel: t('carousel.paginationFirstLabel'),
            paginationLastLabel: t('carousel.paginationLastLabel'),
            paginationGoToViewX: (str: string) => t('carousel.paginationGoToViewX', { pageNumber: str }),
            slideLabel: (num: string, total: string) => `${t('carousel.slideLabel', { itemNumber: num, total })}`,
        }),
        [t],
    );

    const isVideoDisplayed = brandsWithVideo.includes(brand);
    const videoContainerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!isVideoDisplayed || !videoContainerRef.current) return;
        const iframeTitle = t('iframe-title-video');
        const container = videoContainerRef.current;

        let iframeObserver: MutationObserver | null = null;

        const enforceIframeTitle = (iframe: HTMLIFrameElement) => {
            if (iframe.getAttribute('title') !== iframeTitle) {
                iframe.setAttribute('title', iframeTitle);
            }
        };

        const watchIframe = (iframe: HTMLIFrameElement) => {
            enforceIframeTitle(iframe);
            if (!iframeObserver) {
                iframeObserver = new MutationObserver(() => enforceIframeTitle(iframe));
                iframeObserver.observe(iframe, { attributes: true, attributeFilter: ['title'] });
            }
        };

        const containerObserver = new MutationObserver(() => {
            const iframe = container.querySelector('iframe');
            if (iframe) {
                containerObserver.disconnect();
                watchIframe(iframe as HTMLIFrameElement);
            }
        });

        const existingIframe = container.querySelector('iframe');
        if (existingIframe) {
            watchIframe(existingIframe as HTMLIFrameElement);
        } else {
            containerObserver.observe(container, { childList: true, subtree: true });
        }

        return () => {
            containerObserver.disconnect();
            iframeObserver?.disconnect();
        };
    }, [isVideoDisplayed, t]);

    if (!landingPage || !clickHandler) {
        return null;
    }

    const { title, subTitle, text, buttonText, buttonTextAuthenticated, imageUrl, isInverted = false } = landingPage;

    return (
        <>
            <HeroImage
                title={title}
                imageUrl={imageUrl}
                subTitle={subTitle}
                text={text}
                buttonText={isAuthenticated ? buttonTextAuthenticated : buttonText}
                clickHandler={clickHandler}
                inverted={isInverted}
            />
            <ContentSection pageWrap>
                <Heading level={3} className={'u-mb u-text-center'}>
                    {t('title')}
                </Heading>
                <CyCarousel
                    itemAlign="stretch"
                    i18n={carouselI18n}
                    pagination={true}
                    itemsPerView={1}
                    breakpoints={carouselBreakpoints}
                    controlsPosition="outside"
                    style={{ '--cy-carousel-items-padding': '8px' } as React.CSSProperties}
                >
                    {cards.map((card, index) => (
                        <CyCarouselItem key={index}>
                            <CyCard title={card.title} icon={card.icon as never}>
                                <div slot="body">
                                    {card.points.map((point, pointIndex) => (
                                        <IconList key={`list-${pointIndex}`}>
                                            <IconList.Item icon="checkmark" key={pointIndex} className="u-mb-xxsmall">
                                                {point}
                                            </IconList.Item>
                                        </IconList>
                                    ))}
                                </div>
                            </CyCard>
                        </CyCarouselItem>
                    ))}
                </CyCarousel>
            </ContentSection>
            {isVideoDisplayed && (
                <ContentSection pageWrap>
                    <Heading level={2} className={'u-mb u-text-center'}>
                        {t('title-video')}
                    </Heading>
                    <div ref={videoContainerRef}>
                        <Video service="youtube" id="0x_NqoOr47k" />
                    </div>
                </ContentSection>
            )}
        </>
    );
};
